import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable, FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { DropdownOptions, IOption } from "../Dropdown";

export const FailureTypeDetailsDropdown = ({ row }: { row: IEventReportDetails }) => {
    const { i18n } = useTranslation();
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );
    const { loading, failureTypes } = EventReportsEntity.GetFailureTypesOptionsForEdition(
        row.machine_center.machine_center_id,
    );

    const failureTypesOptions: IOption[] = useMemo(
        () =>
            failureTypes.map((failureType) => ({
                id: failureType.failure_type_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureType as unknown as IFailureType,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                ),
            })),
        [i18n, failureTypes],
    );

    const currentSelectionNotInOptions = useMemo(
        () => failureTypesOptions.some((option) => option.id === selectedFailureTypeId),
        [selectedFailureTypeId, failureTypesOptions],
    );

    const handleFailureTypeUpdate = async (id: number) => {
        if (existingEdition !== -1) {
            const failureTypeSelected = failureTypes.find((ft) => ft.failure_type_id === id);
            setEdit((prevEdit) => {
                const newEdit = [...prevEdit];
                newEdit[existingEdition] = {
                    ...newEdit[existingEdition],
                    location_specific: !!failureTypeSelected?.is_location_specific,
                    changes: {
                        ...newEdit[existingEdition]?.changes,
                        failure_type_id: id,
                        area_id: undefined,
                        functional_location_id: undefined,
                        failure_code_id: undefined,
                        ...(user_id && { edited_by_user_id: user_id }),
                    },
                };
                return newEdit;
            });
        }
    };

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : (
        <DropdownOptions
            obligatory={!selectedFailureTypeIdIsNotSelected}
            pending={!Boolean(selectedFailureTypeId) || !currentSelectionNotInOptions}
            inlineStyle={{ width: "80px" }}
            optionSelected={selectedFailureTypeId}
            options={failureTypesOptions}
            handleUpdate={handleFailureTypeUpdate}
        />
    );
};
